import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, normalizeStyle as _normalizeStyle, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["title", "aria-label", "aria-expanded"]
const _hoisted_2 = { class: "tab-label" }
const _hoisted_3 = { class: "dropdown-indicator" }
const _hoisted_4 = {
  key: 0,
  class: "tab-highlight"
}
const _hoisted_5 = { class: "dropdown-content" }
const _hoisted_6 = {
  key: 0,
  class: "dropdown-search"
}
const _hoisted_7 = ["placeholder"]
const _hoisted_8 = { class: "group-label" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = ["innerHTML"]
const _hoisted_11 = { class: "item-label" }
const _hoisted_12 = {
  key: 0,
  class: "item-badge"
}
const _hoisted_13 = {
  key: 1,
  class: "fas fa-check item-check"
}
const _hoisted_14 = ["onClick"]
const _hoisted_15 = ["innerHTML"]
const _hoisted_16 = { class: "item-label" }
const _hoisted_17 = {
  key: 0,
  class: "item-badge"
}
const _hoisted_18 = {
  key: 1,
  class: "fas fa-check item-check"
}
const _hoisted_19 = {
  key: 3,
  class: "no-results"
}

import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import PermissionGuard from '@/components/common/PermissionGuard.vue';

interface Tab {
  id: string;
  name?: string;
  label: string;
  permission?: string | string[];
  permissions?: string[];
  title?: string;
  icon?: string;
  badge?: string | number;
  category?: string;
  [key: string]: any;
}

interface GroupedTab {
  category: string;
  label: string;
  tabs: Tab[];
}

interface Props {
  tabs: Tab[];
  activeTab?: string;
  projectId: string | number;
  enableGrouping?: boolean;
}

interface Emits {
  select: [tabId: string];
}

const MOBILE_BREAKPOINT = 768;
const DROPDOWN_SPACING = 4; // px gap between trigger and desktop menu

// Track whether we're in mobile view and the dropdown's desktop position

export default /*@__PURE__*/_defineComponent({
  __name: 'MoreActionsDropdown',
  props: {
    tabs: {},
    activeTab: { default: '' },
    projectId: {},
    enableGrouping: { type: Boolean, default: false }
  },
  emits: ["select"],
  setup(__props: any, { emit: __emit }) {

/// <reference types="vue" />
// @ts-ignore
const props = __props;

const emit = __emit;
const { t } = useI18n();

const dropdownRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);

// Constants
const isMobileView = ref(false);
const menuPosition = ref({ top: 0, right: 0 });

// Compute the inline style for the menu:
// - On mobile the CSS (bottom-sheet) takes over, so we return nothing.
// - On desktop we use fixed positioning anchored to the trigger button.
const menuStyle = computed<Record<string, string>>(() => {
  if (isMobileView.value) return {};
  return {
    top: `${menuPosition.value.top}px`,
    right: `${menuPosition.value.right}px`,
  };
});

// Recalculate desktop position from the trigger button's bounding rect
const updateMenuPosition = () => {
  isMobileView.value = window.innerWidth <= MOBILE_BREAKPOINT;
  if (!isMobileView.value && triggerRef.value) {
    const rect = triggerRef.value.getBoundingClientRect();
    menuPosition.value = {
      top: rect.bottom + DROPDOWN_SPACING,
      right: window.innerWidth - rect.right,
    };
  }
};

// Helper functions to handle permission arrays
const getTabSinglePermission = (tab: Tab) => {
  // Get permissions from either 'permission', 'permissions', or both
  const tabPermissions: string[] = [];
  
  if (tab.permission) {
    if (Array.isArray(tab.permission)) {
      tabPermissions.push(...tab.permission);
    } else {
      tabPermissions.push(tab.permission);
    }
  }
  
  if (tab.permissions) {
    tabPermissions.push(...tab.permissions);
  }
  
  // If only one permission, return it for single permission check
  return tabPermissions.length === 1 ? tabPermissions[0] : null;
};

const getTabMultiplePermissions = (tab: Tab) => {
  // Get permissions from either 'permission', 'permissions', or both
  const tabPermissions: string[] = [];
  
  if (tab.permission) {
    if (Array.isArray(tab.permission)) {
      tabPermissions.push(...tab.permission);
    } else {
      tabPermissions.push(tab.permission);
    }
  }
  
  if (tab.permissions) {
    tabPermissions.push(...tab.permissions);
  }
  
  // If multiple permissions, return them for anyPermissions check
  return tabPermissions.length > 1 ? tabPermissions : [];
};
const isOpen = ref(false);
const searchQuery = ref('');

// Check if any secondary tab is active
const isAnySecondaryActive = computed(() => {
  return props.tabs.some((tab: Tab) => (tab.name || tab.id) === props.activeTab);
});

// Filter tabs based on search query
const filteredTabs = computed(() => {
  if (!searchQuery.value) return props.tabs;
  
  const query = searchQuery.value.toLowerCase();
  return props.tabs.filter((tab: Tab) => 
    tab.label.toLowerCase().includes(query) ||
    (tab.title && tab.title.toLowerCase().includes(query))
  );
});

// Group tabs by category
const groupedTabs = computed((): GroupedTab[] => {
  if (!props.enableGrouping) return [];
  
  const groups: { [key: string]: GroupedTab } = {};
  
  props.tabs.forEach((tab: Tab) => {
    const category = tab.category || 'other';
    if (!groups[category]) {
      groups[category] = {
        category,
        label: getCategoryLabel(category),
        tabs: []
      };
    }
    groups[category].tabs.push(tab);
  });
  
  return Object.values(groups);
});

// Filter grouped tabs based on search
const filteredGroupedTabs = computed(() => {
  if (!searchQuery.value) return groupedTabs.value;
  
  const query = searchQuery.value.toLowerCase();
  return groupedTabs.value
    .map((group: GroupedTab) => ({
      ...group,
      tabs: group.tabs.filter((tab: Tab) =>
        tab.label.toLowerCase().includes(query) ||
        (tab.title && tab.title.toLowerCase().includes(query))
      )
    }))
    .filter((group: GroupedTab) => group.tabs.length > 0);
});

// Get category label
const getCategoryLabel = (category: string): string => {
  const labels: { [key: string]: string } = {
    collaboration: t('projects.categories.collaboration'),
    management: t('projects.categories.management'),
    finance: t('projects.categories.finance'),
    settings: t('projects.categories.settings'),
    other: t('projects.categories.other')
  };
  return labels[category] || category;
};

// Toggle dropdown
const toggleDropdown = () => {
  if (!isOpen.value) {
    updateMenuPosition();
    searchQuery.value = '';
  }
  isOpen.value = !isOpen.value;
};

// Close dropdown
const closeDropdown = () => {
  isOpen.value = false;
  searchQuery.value = '';
};

// Select tab
const selectTab = (tabId: string) => {
  emit('select', tabId);
  closeDropdown();
};

// Click outside handler
const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    closeDropdown();
  }
};

// Keyboard handler
const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isOpen.value) {
    closeDropdown();
  }
};

// Resize handler – debounced to avoid excessive DOM work during resize
let resizeTimer: ReturnType<typeof setTimeout> | null = null;
const handleResize = () => {
  if (resizeTimer !== null) clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    isMobileView.value = window.innerWidth <= MOBILE_BREAKPOINT;
    if (isOpen.value) {
      closeDropdown();
    }
    resizeTimer = null;
  }, 100);
};

// Watch for active tab changes
watch(() => props.activeTab, () => {
  // Close dropdown when a tab is selected externally
  if (isOpen.value) {
    closeDropdown();
  }
});

onMounted(() => {
  isMobileView.value = window.innerWidth <= MOBILE_BREAKPOINT;
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('keydown', handleKeyDown);
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('keydown', handleKeyDown);
  window.removeEventListener('resize', handleResize);
  if (resizeTimer !== null) clearTimeout(resizeTimer);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "more-actions-dropdown",
    ref_key: "dropdownRef",
    ref: dropdownRef
  }, [
    _createElementVNode("button", {
      ref_key: "triggerRef",
      ref: triggerRef,
      class: _normalizeClass(['more-action-tab', { active: isAnySecondaryActive.value }]),
      onClick: _withModifiers(toggleDropdown, ["stop"]),
      title: _unref(t)('projects.moreActions'),
      "aria-label": _unref(t)('projects.moreActions'),
      "aria-expanded": isOpen.value,
      "aria-haspopup": true,
      type: "button"
    }, [
      _cache[3] || (_cache[3] = _createElementVNode("span", {
        class: "tab-icon",
        style: {"font-size":"1.4em"}
      }, " ⋯ ", -1)),
      _createElementVNode("span", _hoisted_2, _toDisplayString(_unref(t)('projects.more')), 1),
      _createElementVNode("span", _hoisted_3, [
        _createElementVNode("i", {
          class: _normalizeClass(['fas', isOpen.value ? 'fa-chevron-up' : 'fa-chevron-down'])
        }, null, 2)
      ]),
      (isAnySecondaryActive.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_4))
        : _createCommentVNode("", true)
    ], 10, _hoisted_1),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      _createVNode(_Transition, { name: "backdrop-fade" }, {
        default: _withCtx(() => [
          (isOpen.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "mad-backdrop",
                onClick: closeDropdown
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(_Transition, { name: "dropdown-fade" }, {
        default: _withCtx(() => [
          (isOpen.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "mad-menu",
                role: "menu",
                onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"])),
                style: _normalizeStyle(menuStyle.value)
              }, [
                _createElementVNode("div", _hoisted_5, [
                  (_ctx.tabs.length > 8)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                        _cache[4] || (_cache[4] = _createElementVNode("i", { class: "fas fa-search search-icon" }, null, -1)),
                        _withDirectives(_createElementVNode("input", {
                          type: "text",
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
                          placeholder: _unref(t)('projects.searchActions'),
                          class: "search-input",
                          onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
                        }, null, 8, _hoisted_7), [
                          [_vModelText, searchQuery.value]
                        ])
                      ]))
                    : _createCommentVNode("", true),
                  (_ctx.enableGrouping && groupedTabs.value.length > 0)
                    ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(filteredGroupedTabs.value, (group) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: group.category,
                          class: "dropdown-group"
                        }, [
                          _createElementVNode("div", _hoisted_8, _toDisplayString(group.label), 1),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.tabs, (tab) => {
                            return (_openBlock(), _createBlock(PermissionGuard, {
                              key: tab.name || tab.id,
                              permission: getTabSinglePermission(tab),
                              "any-permissions": getTabMultiplePermissions(tab),
                              "project-id": _ctx.projectId
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("button", {
                                  class: _normalizeClass(['dropdown-item', { active: _ctx.activeTab === (tab.name || tab.id) }]),
                                  onClick: ($event: any) => (selectTab(tab.name || tab.id)),
                                  role: "menuitem"
                                }, [
                                  _createElementVNode("span", {
                                    class: "item-icon",
                                    innerHTML: tab.icon
                                  }, null, 8, _hoisted_10),
                                  _createElementVNode("span", _hoisted_11, _toDisplayString(tab.label), 1),
                                  (tab.badge)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(tab.badge), 1))
                                    : _createCommentVNode("", true),
                                  (_ctx.activeTab === (tab.name || tab.id))
                                    ? (_openBlock(), _createElementBlock("i", _hoisted_13))
                                    : _createCommentVNode("", true)
                                ], 10, _hoisted_9)
                              ]),
                              _: 2
                            }, 1032, ["permission", "any-permissions", "project-id"]))
                          }), 128))
                        ]))
                      }), 128))
                    : (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(filteredTabs.value, (tab) => {
                        return (_openBlock(), _createBlock(PermissionGuard, {
                          key: tab.name || tab.id,
                          permission: getTabSinglePermission(tab),
                          "any-permissions": getTabMultiplePermissions(tab),
                          "project-id": _ctx.projectId
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("button", {
                              class: _normalizeClass(['dropdown-item', { active: _ctx.activeTab === (tab.name || tab.id) }]),
                              onClick: ($event: any) => (selectTab(tab.name || tab.id)),
                              role: "menuitem"
                            }, [
                              _createElementVNode("span", {
                                class: "item-icon",
                                innerHTML: tab.icon
                              }, null, 8, _hoisted_15),
                              _createElementVNode("span", _hoisted_16, _toDisplayString(tab.label), 1),
                              (tab.badge)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(tab.badge), 1))
                                : _createCommentVNode("", true),
                              (_ctx.activeTab === (tab.name || tab.id))
                                ? (_openBlock(), _createElementBlock("i", _hoisted_18))
                                : _createCommentVNode("", true)
                            ], 10, _hoisted_14)
                          ]),
                          _: 2
                        }, 1032, ["permission", "any-permissions", "project-id"]))
                      }), 128)),
                  (filteredTabs.value.length === 0 && searchQuery.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                        _cache[5] || (_cache[5] = _createElementVNode("i", { class: "fas fa-search" }, null, -1)),
                        _createElementVNode("p", null, _toDisplayString(_unref(t)('projects.noActionsFound')), 1)
                      ]))
                    : _createCommentVNode("", true)
                ])
              ], 4))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]))
  ], 512))
}
}

})